import React, {useState, useEffect, useRef} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppCustom } from './AppCustom';
import Application from './Application/Application';
import Error from './components/Error/Error';
import Spinner from './components/Spinner/Spinner';
import { useFirebaseData } from './hooks/useFirebaseData';
import { useIframeMessageParser } from './hooks/useIframeMessageParser';
import { useProductsJson } from './hooks/useProductsJson';
import { FIREBASE_UNIT } from './libs/viewer/canvasPixelConvertor';
import { setApiKey } from './redux/actions/apikey';
import { getImgs } from './redux/actions/imglib';
import { setCurrentImage, setMediaLibTitle, setProduct, setProducts } from './redux/actions/product';
import { setSpinner } from './redux/actions/spinner';
import { SET_CONFIGURATION, UPDATE_UNIT_TYPE } from './redux/reducers/settings';
import i18next from 'i18next';
import { DEBUG } from './utilities/logger';
import { openImageLibraries } from './ImageLibrary/redux/actions';
import { registerLicense } from '@syncfusion/ej2-base';
import { addFabricList } from './FabricLibrary/redux/actions';

export const API_KEY = "EV"//"5tDOls2BApP8lIXshTDzLH0kLfp1"

export const CanvasGrid = React.createContext({ rows: 0, columns: 0 })

export const FabricEditor = () => {

    const [apiKey, setFbApikey] = useState(undefined as string | undefined);
    const iFrameData = useIframeMessageParser();
    const firebaseData = useFirebaseData(apiKey);
    const result = useProductsJson(iFrameData?.viewType === 'webpage', firebaseData.userAuthenticated || false);
    const [styleToApply, setStyleToApply] = useState('');
    const [display, setDisplay] = useState(false);
    const dispatch = useDispatch();
    const [canvasGridConfig, setCanvasGridConfig] = useState({ rows: 1, columns: 1 })
    const spinner = useSelector((state:any) => state.spinner)

    const [blockingError, setBlockingError] = useState(undefined as any)
    const APP_VERSION = "1.1.45" + (DEBUG ? '-debug mode' : '');

    registerLicense('Mgo+DSMBaFt+QHJqUE1hXk5Hd0BLVGpAblJ3T2ZQdVt5ZDU7a15RRnVfR1xqSXlXcEZkUXpYeA==;Mgo+DSMBPh8sVXJ1S0R+WVpFdEBBXHxAd1p/VWJYdVt5flBPcDwsT3RfQF5jTH9adkJnX3xZeHNUTg==;ORg4AjUWIQA/Gnt2VFhiQlRPd11dXmJWd1p/THNYflR1fV9DaUwxOX1dQl9gSXtSf0VkXXldd3xdQ2g=;MjExNTY2MUAzMjMxMmUzMjJlMzVKR3pvd2pDN1VPbTlEdE81WHVOeE5WaGFtZ25RN3hSTTFBVlZqdHoxZkRnPQ==;MjExNTY2MkAzMjMxMmUzMjJlMzVYbXBackFZaE1tSnBUZEYyOTNDM3JFMVhGbldWSDliTlFLaHlFcDBBczJZPQ==;NRAiBiAaIQQuGjN/V0d+Xk9BfV5AQmBIYVp/TGpJfl96cVxMZVVBJAtUQF1hSn5Wd0xjXntYc3NcQGZU;MjExNTY2NEAzMjMxMmUzMjJlMzVvTE93a1dFK2tRWmJ5aU5naEh5bDY1Qk5EUW5zVjh0YWt2Ni8zekpiVkpFPQ==;MjExNTY2NUAzMjMxMmUzMjJlMzVEZXhMNk9NVE14ekxMZjF2ZU1JR1ZmendlbnNHamJCUnVKUG1RYzhWUHZRPQ==;Mgo+DSMBMAY9C3t2VFhiQlRPd11dXmJWd1p/THNYflR1fV9DaUwxOX1dQl9gSXtSf0VkXXldeHRRRWg=;MjExNTY2N0AzMjMxMmUzMjJlMzVCbUZKeVJ4R2VlMTkvekg0UWpJS1M0MERLaVJXa3hZU1F6c0NyY0gwZ0NrPQ==;MjExNTY2OEAzMjMxMmUzMjJlMzVWVEV3Z2xVMUZ1TTBjR29oSWlSNFBoSzNNS21BZzhIbDZVL0ZjVmhmd1A4PQ==;MjExNTY2OUAzMjMxMmUzMjJlMzVvTE93a1dFK2tRWmJ5aU5naEh5bDY1Qk5EUW5zVjh0YWt2Ni8zekpiVkpFPQ==');

    const appVersion = {
        version:APP_VERSION
    };

    useEffect(()=>{
        const langSetting = iFrameData.languageSetting || 'english'
        if(langSetting){
            i18next.changeLanguage(langSetting)
        }
    },[iFrameData.languageSetting]);

    useEffect(() => {
        if (iFrameData.viewType === 'webpage') {
            setDisplay(true);
        } else if (iFrameData.cssLink) {
            const link = document.createElement('link');
            link.rel = "stylesheet";
            link.type = "text/css";
            link.href = iFrameData.cssLink;
            document.head.appendChild(link);
            setStyleToApply("custom"); 
            link.onload = function () {
                setDisplay(true);
            }
        } else if(iFrameData.viewType === 'iframe' || iFrameData.viewType === 'embedded'){
            setDisplay(true);
        }
    }, [iFrameData.viewType, iFrameData.cssLink]);

    useEffect(() => {
        if (iFrameData.viewType === 'webpage') {
            setFbApikey(API_KEY)
            dispatch(setApiKey(API_KEY))
        } else if (iFrameData.apiKey) {
            setFbApikey(iFrameData.apiKey)
            dispatch(setApiKey(iFrameData.apiKey))
        }
    }, [iFrameData.viewType, iFrameData.apiKey]);

    useEffect(() => {
        let unitType =  firebaseData.config?.unitType;
        if (unitType) {
            dispatch({
                type: UPDATE_UNIT_TYPE,
                payload: { unitType }
            });
            FIREBASE_UNIT.unit = unitType;
        }
    }, [firebaseData.config?.unitType])

    useEffect(() => {
        const { columns, rows } = iFrameData.gridConfig || {}
        if (rows && columns) {
            setCanvasGridConfig({ rows, columns })
        }
    }, [iFrameData.gridConfig])

    useEffect(() => {
        if (iFrameData.imgLib) {
            dispatch(getImgs(iFrameData.imgLib))
        }
    }, [iFrameData.imgLib]);

    useEffect(()=>{
        if(iFrameData.categoryId){
            const data = iFrameData.categoryId.split('|')
            const id = data[0];
            const name = data[1];
            const patternContainer = data[2] === 'true'
            dispatch(openImageLibraries(id,name, patternContainer));
        }
    },[iFrameData.categoryId])

    useEffect(()=>{
        iFrameData.currentImage && dispatch(setCurrentImage(iFrameData.currentImage))
    },[iFrameData.currentImage])

    useEffect(()=>{
        iFrameData.mediaLibTitle && dispatch(setMediaLibTitle(iFrameData.mediaLibTitle))
    },[iFrameData.mediaLibTitle])

    useEffect(() => {
        if (iFrameData.fabricList?.length) {
            dispatch(setProducts(iFrameData.fabricList));
            dispatch(addFabricList(iFrameData.fabricList))
            dispatch(setProduct({
                ...iFrameData.fabricList[0],
                sku: iFrameData.sku ?? "notfound",
                isPrimary: true,
                ...iFrameData.fabricList[0]?.orderOptions?.length && {
                    productID: iFrameData.fabricList[0]?.orderOptions[0].productID,
                    width: iFrameData.fabricList[0]?.orderOptions[0].width,
                    totalLength: iFrameData.fabricList[0]?.orderOptions[0].totalLength,
                    price: iFrameData.fabricList[0]?.orderOptions[0].price,
                    basePrice: iFrameData.fabricList[0]?.orderOptions[0].price,
                    optionName: iFrameData.fabricList[0]?.orderOptions[0].name
                }
            }));
        }
    }, [iFrameData.fabricList]);

    useEffect(() => {
        if (iFrameData.currentFabric ) {
            dispatch(
              setProduct({
                ...iFrameData.currentFabric,
                isCurrentFabric: true,
                sku: iFrameData.sku ?? "notfound",
                isPrimary: true,
                ...iFrameData.currentFabric?.orderOptions?.length && {
                    productID: iFrameData.currentFabric?.orderOptions[0].productID,
                    width: iFrameData.currentFabric?.orderOptions[0].width,
                    totalLength: iFrameData.currentFabric?.orderOptions[0].totalLength,
                    price: iFrameData.currentFabric?.orderOptions[0].price,
                    basePrice: iFrameData.currentFabric?.orderOptions[0].price,
                    optionName: iFrameData.currentFabric?.orderOptions[0].name,
                }
              }),
            )
          }
    }, [iFrameData.currentFabric])

    const product = useSelector((state: any) => state.product)

    const stepLength = firebaseData.config?.fabricUnitSizeCm || 1;
    const { rows, columns } = canvasGridConfig;
    const fabricUnit = firebaseData.config?.unitType;

    useEffect(() => {
        setBlockingError(firebaseData.error || result.error)
    },[firebaseData.error, result.error])

    const settings = useSelector((state: any) => state?.settings)

    useEffect(() => {
        if (stepLength && fabricUnit && rows && columns) {
            let updateCurrentLength = !product?.isPrimary ? product?.totalLength : 0
            if (!updateCurrentLength && ((settings?.display?.currentLength || 0) < stepLength))
                updateCurrentLength = stepLength
            dispatch({
                type: SET_CONFIGURATION,
                display:{
                    currentLength: stepLength,
                },
                payload: {
                    updateCurrentLength,
                    stepSize: stepLength < product?.totalLength ? stepLength : product?.totalLength,
                    stepUnitType: fabricUnit,
                    split: (rows || columns) ? {
                        rows,
                        columns
                    } : undefined
                }
            })
        }
    }, [stepLength, fabricUnit, rows, columns, product])

    // console.log('USE CUSTOM APP:'+firebaseData?.config?.useCustomApp);

    return display ?
       <div>
        <Spinner active={spinner} container=''/>
        {blockingError && <Error text={`ERROR: ${blockingError}`}/>}
        {!blockingError && firebaseData.config && <CanvasGrid.Provider value={canvasGridConfig}>
            {firebaseData.config.useCustomApp?<AppCustom {...appVersion} styleToApply={styleToApply} />:<Application {...appVersion}/>}
        </CanvasGrid.Provider>}
        </div>
        : null

}
